import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardService } from '@core/dashboard/dashboard.service';
import { SalesTargetDialogData } from '@core/dashboard/dashboard.types';
import { UserType } from '@core/users/users.types';
import { UsersService } from '@core/users/users.service';
import { TranslocoService } from '@ngneat/transloco';
import { take } from 'rxjs';

/**
 * Sets or edits one salesperson's target for the CURRENT calendar month (spec §0.3) —
 * the month every dashboard figure refers to. The backend upserts by (userId, year,
 * month), so create and edit are the same mutation; the UI still tells them apart
 * (title, hint, button label) so a manager knows whether they are overwriting a target.
 */
@Component({
  selector: 'app-dialog-sales-target',
  templateUrl: './dialog-sales-target.component.html',
  styleUrls: ['./dialog-sales-target.component.scss'],
})
export class DialogSalesTargetComponent implements OnInit {
  form: FormGroup;
  users: UserType[] = [];
  usersError = false;
  saving = false;
  errorMessage: string | null = null;

  readonly year: number;
  readonly month: number;

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _dashboardService: DashboardService,
    private readonly _usersService: UsersService,
    private readonly _dialogRef: MatDialogRef<DialogSalesTargetComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SalesTargetDialogData,
    private readonly _translocoService: TranslocoService,
  ) {
    // The backend's getSalesTarget/getLeaderboard resolve "current month" in UTC — matching
    // that here (instead of local time) keeps create-vs-edit and the saved month in sync with
    // the leaderboard data near a month boundary (spec §0.3).
    const today = new Date();
    this.year = today.getUTCFullYear();
    this.month = today.getUTCMonth() + 1;
  }

  ngOnInit(): void {
    const presetUserId = this.data.user?.id ?? null;

    this.form = this._formBuilder.group({
      userId: [presetUserId, Validators.required],
      targetValue: [this._existingTargetFor(presetUserId), [Validators.required, Validators.min(1)]],
    });

    // Picking another salesperson swaps between create (empty) and edit (pre-filled).
    this.form.get('userId').valueChanges.subscribe((userId) => {
      this.form.get('targetValue').setValue(this._existingTargetFor(userId));
    });

    if (!this.data.user) {
      this._usersService
        .getUsers()
        .pipe(take(1))
        .subscribe({
          next: (users) => (this.users = users ?? []),
          error: () => (this.usersError = true),
        });
    }
  }

  /** The selected salesperson's current-month target, or null when none is set yet (null or 0). */
  get existingTarget(): number | null {
    return this._existingTargetFor(this.form?.get('userId')?.value ?? null);
  }

  get isEditing(): boolean {
    return this.existingTarget !== null;
  }

  idOf(user: UserType): number {
    return Number(user.id);
  }

  submit(): void {
    if (this.saving) {
      return;
    }
    this.errorMessage = null;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { userId, targetValue } = this.form.getRawValue();
    this.saving = true;
    this._dashboardService
      .setSalesTarget({
        userId: Number(userId),
        year: this.year,
        month: this.month,
        targetValue: Number(targetValue),
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this._dialogRef.close(true);
        },
        error: () => {
          this.saving = false;
          this.errorMessage = this._translocoService.translate('dashboard.salesTargetDialog.saveError');
        },
      });
  }

  cancel(): void {
    this._dialogRef.close(false);
  }

  private _existingTargetFor(userId: number | null): number | null {
    if (userId === null || userId === undefined) {
      return null;
    }
    const value = this.data.currentTargets?.[Number(userId)];
    return value !== null && value !== undefined && value > 0 ? value : null;
  }
}
